import { useEffect, useRef } from "react";
import { Flex, Box } from "@mantine/core";
import { useFormStore } from "@/store/form-builder";
import { useAssistantStore } from "@/store/form-builder/useAssistantStore";
import { editorVisitKey } from "@/store/form-builder/tabStorage";
import {
  useAssistantHandoffStore,
  type BuildHandoff,
} from "@/store/form-builder/useAssistantHandoffStore";
import type { FormBuilderVisibility } from "@/api/form-builder/formBuilder.types";
import {
  Header,
  type FallbackDefaultControl,
  type ReadOnlyReason,
  type SaveStatus,
} from "../Shared/Header";
import { LeftPanel } from "../LeftPanel/LeftPanel";
import { FormEditor } from "../FormEditor/FormEditor";
import { RightPanel } from "../RightPanel/RightPanel";
import { PreviewMode } from "../Preview/PreviewMode";
import { PanelEdge } from "./PanelEdge";
import { NarrowEditorLayout } from "./NarrowEditorLayout";
import { useIsNarrowEditor } from "../../hooks/useIsNarrowEditor";
import { useEditorPanels } from "../../hooks/useEditorPanels";
import { AssistantDock } from "../../Assistant/AssistantDock";
import { AssistantPanel } from "../../Assistant/AssistantPanel";
import { useFormAssistantAccess } from "../../Assistant/useFormAssistantAccess";
import { EditorProposalBanner } from "./EditorProposalBanner";
import styles from "./FormBuilder.module.scss";

interface FormBuilderProps {
  initialData?: any[] | string;
  onChange?: (json: string) => void;
  onPublish?: () => void | Promise<void>;
  isSaving?: boolean;
  isPublishing?: boolean;
  formName?: string;
  formId?: string;
  tenantId?: string;
  /** True for a global form viewed outside the ServEngine super admin scope, an archived form, or a past version. */
  readOnly?: boolean;
  readOnlyReason?: ReadOnlyReason;
  viewingVersion?: number;
  /** Present only for the ServEngine-scope super admin editing a platform-owned form. */
  visibility?: FormBuilderVisibility;
  onVisibilityChange?: (next: FormBuilderVisibility) => void;
  fallbackDefault?: FallbackDefaultControl;
  editingPublishedForm?: boolean;
  saveStatus?: SaveStatus;
  saveError?: string;
  hasUnpublishedChanges?: boolean;
  confirmPublish?: boolean;
  /** Where publishing will assign this form, when it was created for an assignment slot. */
  assignTo?: string;
  isAssigning?: boolean;
}

export function FormBuilder({
  initialData,
  onChange,
  onPublish,
  isSaving,
  isPublishing,
  formName,
  formId,
  tenantId,
  readOnly,
  readOnlyReason,
  viewingVersion,
  visibility,
  onVisibilityChange,
  fallbackDefault,
  editingPublishedForm,
  saveStatus,
  saveError,
  hasUnpublishedChanges,
  confirmPublish,
  assignTo,
  isAssigning,
}: FormBuilderProps) {
  const isPreview = useFormStore((s) => s.isPreview);
  const pages = useFormStore((s) => s.pages);
  const initFromJSON = useFormStore((s) => s.initFromJSON);
  const isNarrow = useIsNarrowEditor();
  const assistantEnabled = useFormAssistantAccess() && !readOnly;
  const isAssistantOpen = useAssistantStore((s) => s.isOpen);
  const assistantDock =
    assistantEnabled && isAssistantOpen ? (
      <AssistantDock>
        <AssistantPanel tenantId={tenantId} />
      </AssistantDock>
    ) : null;
  const panels = useEditorPanels();

  useEffect(() => {
    if (initialData) initFromJSON(initialData);
  }, []);

  useEffect(() => {
    if (onChange) onChange(useFormStore.getState().getCompactJSON());
  }, [pages, onChange]);

  // The assistant store is app-wide; a proposal must never follow the user to another form.
  // The chat is bound to this form, so a reload (or a remount in development) keeps
  // it and another form starts clean. A new form is known by the page it opened on.
  // The hand-off is taken once and kept, so a remount builds the same plan again.
  const buildHandoff = useRef<BuildHandoff | null | undefined>(undefined);
  useEffect(() => {
    const assistant = useAssistantStore.getState();
    buildHandoff.current ??= useAssistantHandoffStore.getState().takeBuild(formId);
    const build = buildHandoff.current;
    if (build) assistant.reset();
    assistant.bindTo(formId ?? `new:${editorVisitKey()}`);
    if (build && !readOnly) {
      if (build.plan) assistant.startFromPlan(build.brief, build.plan, build.said);
      else assistant.startFromRequest(build.brief, build.said);
      useFormStore.getState().setPreviewPageIndex(0);
      useFormStore.getState().setIsPreview(true);
    }
    // The form store is app-wide too: the next form opens in the editor, not the preview.
    return () => {
      useFormStore.getState().setIsPreview(false);
    };
  }, []);

  // A new draft gets its id on its first save; it is still the same form.
  const boundFormId = useRef(formId);
  useEffect(() => {
    if (boundFormId.current === formId || !formId) return;
    const isFirstSave = boundFormId.current === undefined;
    boundFormId.current = formId;
    if (isFirstSave) useAssistantStore.getState().rebind(formId);
    else useAssistantStore.getState().bindTo(formId);
  }, [formId]);

  if (isPreview)
    return (
      <PreviewMode
        tenantId={tenantId}
        readOnly={readOnly}
        onPublish={onPublish}
        isPublishing={isPublishing}
        confirmPublish={confirmPublish}
      />
    );

  return (
    <Flex direction="column" bg="#e2e6eb" className={isNarrow ? styles.rootNarrow : styles.root}>
      <Header
        title={formName || "Untitled Form"}
        onPublish={onPublish}
        isSaving={isSaving}
        isPublishing={isPublishing}
        formId={formId}
        tenantId={tenantId}
        readOnly={readOnly}
        readOnlyReason={readOnlyReason}
        viewingVersion={viewingVersion}
        visibility={visibility}
        onVisibilityChange={onVisibilityChange}
        fallbackDefault={fallbackDefault}
        editingPublishedForm={editingPublishedForm}
        saveStatus={saveStatus}
        saveError={saveError}
        hasUnpublishedChanges={hasUnpublishedChanges}
        confirmPublish={confirmPublish}
        assignTo={assignTo}
        isAssigning={isAssigning}
      />
      {assistantEnabled && <EditorProposalBanner />}
      {isNarrow ? (
        <Flex flex={1} mih={0} direction="column" style={{ overflow: "hidden" }}>
          <NarrowEditorLayout />
          {assistantDock}
        </Flex>
      ) : (
        <Flex flex={1} mih={0} style={{ overflow: "hidden" }}>
          {/* The edge comes first in the DOM so its hide control is reached before the palette
              with Tab; `order` keeps the panel visually on the left. */}
          <PanelEdge side="left" panel={panels.left} />
          {!panels.left.collapsed && (
            <Box style={{ width: panels.left.width, flexShrink: 0, height: "100%", order: -1 }}>
              <LeftPanel />
            </Box>
          )}
          <FormEditor />
          <PanelEdge side="right" panel={panels.right} />
          {!panels.right.collapsed && (
            <Box style={{ width: panels.right.width, flexShrink: 0, height: "100%" }}>
              <RightPanel />
            </Box>
          )}
          {assistantDock}
        </Flex>
      )}
    </Flex>
  );
}
